import { useContext, useState, useEffect, useRef } from 'react'
import dayjs from 'dayjs'
import { ApiContext } from '../../../contexts/api-context'
import { LoginContext } from '../../../contexts/login-context'
import { ModalContext } from '../../../contexts/modal-context'
import { UserBooking, BookedSeat, AndiUserProfile, BusinessUnit } from '../../../types/andTypes'
import {
  formatToReadableDate,
  idToNameString,
  generateIcsFile,
  getInitials
} from '../../../utilities/utils'
import {
  BookingDate,
  BookingDetailsContainer,
  BookingTitle,
  DetailsWrapper,
  BookingChipContainer,
  ErrorText
} from './group-booking-styles'
import DecisionPopup from '../../modal/popup/decision-popup/decision-popup'
import ResultPopup from '../../modal/popup/result-popup/result-popup'
import LoadingPopup from '../../modal/popup/loading-popup/loading-popup'
import {
  IconWrapper,
  GroupBookingHeader,
  GroupBookingInfo,
  NumberOfAttendeesText,
  NumberOfAttendees,
  Bin
} from '../../user-bookings/booking-list-styles'
import logAnalyticsEvent from '../../../utilities/firebase-analytics'
import GroupBookingChip from '../../atoms/Chip/group-booking-chip'
import bin from '../../user-bookings/assets/bin.svg'
import { BiChevronDown, BiChevronUp } from 'react-icons/bi'
import GroupBookingOptions from '../DropDown/group-booking-options'
import ReassignPopup from '../../modal/popup/reassign-popup/reassign-popup'
import GroupBookingPopup from '../../modal/popup/group-booking-popup/group-booking-popup'

type ContentCardProps = {
  userBooking: UserBooking
  className?: string
}

const GroupBooking = ({ userBooking, className }: ContentCardProps) => {
  const { businessUnits, getSeatBookingOnDate } = useContext(ApiContext)
  const selectedBUId = userBooking.clubId
  const selectedBU: BusinessUnit | undefined = businessUnits?.find(bu => bu.id === selectedBUId)
  const selectedLocation = selectedBU?.name + ', ' + selectedBU?.location
  const [BuANDis, setBuANDis] = useState<AndiUserProfile[]>([])
  const { user } = useContext(LoginContext)
  const { apiClient, userProfile, getMyGroupBookingMembers, getTeams, getANDis } =
    useContext(ApiContext)
  const { setModalOpen, setModalContent } = useContext(ModalContext)
  const displayName = businessUnits && businessUnits.find(bU => bU.id === userBooking.clubId)?.name
  const [open, setOpen] = useState(false)
  const [dropDownOpen, setDropDownOpen] = useState(false)
  const [groupMembers, setGroupMembers] = useState<BookedSeat[]>([
    { name: '', initials: '', businessUnit: '', userId: '', bookingUserId: '' }
  ])
  const selectedDateRef = useRef(new Date())

  useEffect(() => setBuANDis(getANDis), [selectedBU?.id])
  useEffect(() => {
    const group = getMyGroupBookingMembers(userBooking.groupId, new Date(userBooking.date))

    if (group) setGroupMembers(group)
  }, [userBooking])
  const toggle = () => {
    setOpen(!open)
  }
  const cancelBooking = async (
    date: string,
    id: string | undefined,
    isSingle: boolean,
    name: string,
    bookingUserId: string
  ) => {
    setModalContent(<LoadingPopup />)

    const startTime = new Date().getTime()

    try {
      if (!user?.email) {
        return Promise.reject(new Error('Error cancelling booking'))
      }

      const isOrganiser = userProfile?.userId === bookingUserId

      isSingle
        ? await apiClient.deleteUserBooking({
            userId: id,
            dateBooked: date,
            bookingUserId,
            isOrganiser
          })
        : await apiClient.deleteGroupBooking({
            groupId: id,
            dateBooked: date,
            bookingUserId
          })

      logAnalyticsEvent('booking_deletion_success', {
        latency_ms: new Date().getTime() - startTime,
        user: user?.email,
        team: userProfile?.team,
        default_location: userProfile?.businessUnit
      })
      const headerText = isSingle ? 'Group Member Removed' : 'Booking Cancelled'
      const resultMessage = isSingle
        ? `${name} was successfuly removed.`
        : 'Your group booking has been canceled.'
      setModalContent(
        <ResultPopup headerText={headerText} success={true}>
          <p>{resultMessage}</p>
        </ResultPopup>
      )
    } catch (error) {
      let message = ''
      logAnalyticsEvent('booking_deletion_error', {
        latency_ms: new Date().getTime() - startTime,
        user: user?.email,
        team: userProfile?.team,
        default_location: userProfile?.businessUnit
      })

      if (
        error instanceof Error &&
        error.message.match(/The seat you are trying to cancel does not exist/)
      ) {
        message = error.message
      } else {
        message = 'Something went wrong. Please try to cancel your booking again.'
      }

      setModalContent(
        <ResultPopup headerText="Oh no!" success={false}>
          <p>{message}</p>
        </ResultPopup>
      )
    }
  }

  const reassignOrganiser = async (
    booking: UserBooking,
    newOrganiser: string,
    oldOrganiserId: string
  ) => {
    setModalContent(<LoadingPopup />)

    const startTime = new Date().getTime()

    try {
      await apiClient.reAssignGroupOrganiser({
        groupId: booking.groupId,
        dateBooked: booking.date,
        newOrganiserUserId: newOrganiser,
        oldOrganiserId
      })

      logAnalyticsEvent('reassign_decision', {
        user: user?.email,
        team: userProfile?.team,
        default_location: userProfile?.businessUnit
      })

      const resultMessage = `${idToNameString(
        newOrganiser
      )} is now the new organiser of the group booking.`

      setModalContent(
        <ResultPopup headerText={'Organiser was changed'} success={true}>
          <p>{resultMessage}</p>
        </ResultPopup>
      )
    } catch (error) {
      let message = ''
      logAnalyticsEvent('booking_organiser_change_error', {
        latency_ms: new Date().getTime() - startTime,
        user: user?.email,
        team: userProfile?.team,
        default_location: userProfile?.businessUnit
      })

      if (
        error instanceof Error &&
        error.message.match(/The seat you are trying to cancel does not exist/)
      ) {
        message = error.message
      } else {
        message = 'Something went wrong. Please try to re-arrange your booking again.'
      }

      setModalContent(
        <ResultPopup headerText="Oh no!" success={false}>
          <p>{message}</p>
        </ResultPopup>
      )
    }
  }

  const cancelAndReassignOrganiser = async (
    booking: UserBooking,
    userId: string,
    newOrganiser: string
  ) => {
    setModalContent(<LoadingPopup />)

    const startTime = new Date().getTime()

    try {
      await apiClient.reAssignGroupOrganiser({
        groupId: booking.groupId,
        dateBooked: booking.date,
        newOrganiserUserId: newOrganiser,
        includeFlag: true,
        oldOrganiserId: userProfile?.userId || ''
      })

      await apiClient.deleteUserBooking({ userId, dateBooked: booking.date })

      logAnalyticsEvent('booking_organiser_cancel_and_change_decision', {
        user: user?.email,
        team: userProfile?.team,
        default_location: userProfile?.businessUnit
      })

      const resultMessage = `Your desk has been cancelled and ${idToNameString(
        newOrganiser
      )} is now the new organiser of the group booking.`

      setModalContent(
        <ResultPopup headerText={'Desk Cancelled'} success={true}>
          <p>{resultMessage}</p>
        </ResultPopup>
      )
    } catch (error) {
      let message = ''
      logAnalyticsEvent('booking_organiser_cancel_and_change_error', {
        latency_ms: new Date().getTime() - startTime,
        user: user?.email,
        team: userProfile?.team,
        default_location: userProfile?.businessUnit
      })

      if (
        error instanceof Error &&
        error.message.match(/The seat you are trying to cancel does not exist/)
      ) {
        message = error.message
      } else {
        message = 'Something went wrong. Please try to re-arrange your booking again.'
      }

      setModalContent(
        <ResultPopup headerText="Oh no!" success={false}>
          <p>{message}</p>
        </ResultPopup>
      )
    }
  }

  const onDelete = (booking: UserBooking) => {
    setModalOpen(true)
    logAnalyticsEvent('delete_decision', {
      user: user?.email,
      team: userProfile?.team,
      default_location: userProfile?.businessUnit
    })
    setModalContent(
      <DecisionPopup
        header="Cancel Group Booking"
        decisionText="Are you sure you want to cancel your entire group booking?"
        bookingDetails={booking}
        confirmAction={() =>
          cancelBooking(booking.date, booking.groupId, false, '', booking.bookingUserId || '')
        }
      />
    )
  }

  const reassignBooking = (booking: UserBooking) => {
    groupMembers.shift()
    setModalOpen(true)
    setModalContent(
      <ReassignPopup
        header="Change Organiser"
        decisionText="Please select from below who will be the new organiser of this group booking"
        groupMembers={groupMembers}
        confirmAction={selectedMember =>
          reassignOrganiser(booking, selectedMember, userProfile?.userId || '')
        }
      />
    )
  }

  const cancelOrganiserDesk = (booking: UserBooking, userId: string) => {
    groupMembers.shift()
    setModalOpen(true)
    setModalContent(
      <ReassignPopup
        header="Desk Cancellation"
        decisionText={`You have chosen to cancel your desk whilst being the organiser.
        \nPlease select from below who will be the new organiser of this group booking.`}
        groupMembers={groupMembers}
        confirmAction={selectedMember =>
          cancelAndReassignOrganiser(booking, userId, selectedMember)
        }
        warn
      />
    )
  }

  const onDeleteGroupMember = (
    userId: string,
    dateBooked: string,
    name: string,
    bookingUserId: string
  ) => {
    setModalOpen(true)
    logAnalyticsEvent('delete_group_member_decision', {
      user: user?.email,
      team: userProfile?.team,
      default_location: userProfile?.businessUnit
    })
    const decisionText =
      userProfile?.userId === userId
        ? `Are you sure you want to cancel your booking?`
        : `Are you sure you want to remove ${name} from this booking?`
    const headerText = userProfile?.userId === userId ? `Cancel Booking` : `Remove Group Member`
    setModalContent(
      <DecisionPopup
        header={headerText}
        decisionText={decisionText}
        bookingDetails={userBooking}
        confirmAction={() => cancelBooking(dateBooked, userId, true, name, bookingUserId)}
      />
    )
  }

  const isDeletable = (userId: string, bookingOwner: string | undefined) => {
    return !!(userProfile?.userId === userId || userProfile?.userId === bookingOwner)
  }

  const amendGroupBookingModal = (dateString: string, selectedMembers: AndiUserProfile[]) => {
    setModalContent(
      <GroupBookingPopup
        type="amend"
        header="Group"
        dateString={dateString}
        selectedLocation={selectedLocation}
        confirmGroupAction={confirmGroupBooking}
        ANDis={BuANDis}
        organiser={userProfile}
        availableSeats={
          (selectedBU && selectedBU?.deskCapacity - buSeatsBookedOnDate().length) || -1
        }
        selectedANDis={selectedMembers}
        teams={getTeams(selectedBU?.id || '', userProfile?.businessUnit || '')}
        selectedBU={selectedBU}
      />
    )
  }

  const confirmGroupBooking = async (members: AndiUserProfile[]) => {
    if (!user?.email || !selectedBU) return
    setModalContent(<LoadingPopup />)

    const dateString = dayjs(selectedDateRef.current).format('YYYY-MM-DD')
    const startTime = new Date().getTime()

    // find removed members
    const removedMembers = groupMembers
      .filter(member => !members.find(m => m.userId === member.userId))
      .map(({ userId }) => userId)

    const { bookingUserId, groupId } = groupMembers[0]

    // find added members
    const addedMembers = members
      .filter(member => !groupMembers.find(m => m.userId === member.userId))
      .map(({ name, userId }) => ({
        bookingUserId,
        groupId,
        businessUnit: selectedBU.id,
        initials: getInitials(name || ''),
        name: name || '',
        userId
      }))

    try {
      await apiClient.updateGroupBooking(
        addedMembers,
        removedMembers,
        dateString,
        userProfile?.userId || ''
      )
      logAnalyticsEvent('group_booking_creation_success', {
        latency_ms: new Date().getTime() - startTime,
        user: user?.email,
        team: userProfile?.team,
        booking_location: selectedBU.id,
        default_location: userProfile?.businessUnit
      })

      setModalContent(
        <ResultPopup
          headerText="Yay! Booking Confirmed"
          success={true}
          actionButtonConfig={{
            text: 'Download booking',
            isCircle: false,
            onClick: () => generateIcsFile(dateString, selectedBU)
          }}
        >
          <p>
            Your group booking is confirmed.
            <br />
            Download ICS file to add your booking to your calendar.
          </p>
        </ResultPopup>
      )
    } catch (error) {
      let message = ''
      logAnalyticsEvent('group_booking_creation_error', {
        latency_ms: new Date().getTime() - startTime,
        user: user?.email,
        team: userProfile?.team,
        booking_location: selectedBU.id,
        default_location: userProfile?.businessUnit
      })
      if (error instanceof Error) {
        message = error.message
      } else {
        message = 'Something went wrong. Please try to book your desk again.'
      }

      setModalContent(
        <ResultPopup
          actionButtonConfig={{
            text: 'Amend booking',
            isPrimary: true,
            isCircle: false,
            onClick: () => amendGroupBookingModal(dateString, members)
          }}
          headerText={'Oh no!'}
          success={false}
        >
          <ErrorText>
            Unfortunately your group booking was unsuccessful:{'\n'}
            {message}
          </ErrorText>
        </ResultPopup>
      )
    }
  }

  const onEditBookings = (booking: UserBooking) => {
    const selectedANDis: AndiUserProfile[] = groupMembers.map(member => {
      return {
        id: member.userId,
        userId: member.userId,
        businessUnit: member.businessUnit,
        team: member.businessUnit,
        name: member.name
      }
    })

    selectedDateRef.current = new Date(booking.date)
    const seatsBookedOnDateForAllBU = getSeatBookingOnDate(new Date(selectedDateRef.current))
    const seatsBookedOnDate = seatsBookedOnDateForAllBU?.seats.filter(
      booking => booking.businessUnit === userBooking.clubId
    )
    setModalOpen(true)
    setModalContent(
      <GroupBookingPopup
        type="edit"
        header="Group"
        dateString={booking.date}
        selectedLocation={selectedLocation}
        confirmGroupAction={confirmGroupBooking}
        ANDis={BuANDis}
        organiser={userProfile}
        availableSeats={
          (selectedBU &&
            seatsBookedOnDate &&
            selectedBU?.deskCapacity - seatsBookedOnDate.length + selectedANDis.length) ||
          -1
        }
        teams={getTeams(selectedBU?.id || '', userProfile?.businessUnit || '')}
        selectedANDis={selectedANDis}
        bookedAndis={buSeatsBookedOnDate()}
        selectedBU={selectedBU}
      />
    )
  }

  const buSeatsBookedOnDate = () => {
    const bookingsOnDate = getSeatBookingOnDate(selectedDateRef.current)
    const todaysSeats = bookingsOnDate?.seats || []
    return todaysSeats.filter(seat => seat.businessUnit === selectedBU?.id)
  }

  return (
    <BookingDetailsContainer data-testid="booking-details" className={className}>
      <GroupBookingHeader>
        <DetailsWrapper>
          <BookingTitle>{formatToReadableDate(userBooking.date)}</BookingTitle>
          <BookingDate>{displayName}</BookingDate>
        </DetailsWrapper>
        <IconWrapper>
          {/*  */}
          {/* <BookingImgGroup src={GroupBookingLabel} /> */}
          {userProfile?.userId !== groupMembers[0].bookingUserId ? (
            <Bin
              data-testid="Delete"
              role="button"
              title={'Remove yourself from group booking'}
              disabled={false}
              src={bin}
              width={'25px'}
              height={'25px'}
              onClick={() =>
                userProfile?.userId !== undefined
                  ? onDeleteGroupMember(
                      userProfile.userId,
                      userBooking.date,
                      idToNameString(userProfile.userId),
                      userProfile.userId
                    )
                  : alert('Invalid userID')
              }
            />
          ) : (
            <GroupBookingOptions
              data-testid="group-booking-options"
              expanded={dropDownOpen}
              onToggle={(open: boolean) => setDropDownOpen(open)}
              optionButtons={[
                { title: 'Cancel Entire Booking', onClick: () => onDelete(userBooking) },
                {
                  title: 'Cancel My Desk',
                  onClick: () =>
                    userProfile?.userId !== undefined
                      ? cancelOrganiserDesk(userBooking, userProfile.userId)
                      : alert('Invalid userID'),
                  disabled: groupMembers.length <= 1
                },
                {
                  title: 'Change Organiser',
                  onClick: () => reassignBooking(userBooking),
                  disabled: groupMembers.length <= 1
                },
                { title: 'Edit Booking', onClick: () => onEditBookings(userBooking) }
              ]}
            />
          )}
        </IconWrapper>
      </GroupBookingHeader>
      <GroupBookingInfo>
        <GroupBookingChip
          userName={`${groupMembers[0].name}`}
          isOrganiser={true}
          deletable={false}
          assignable={false}
        />
        <NumberOfAttendees>
          <NumberOfAttendeesText>{`${groupMembers.length} Attendees`}</NumberOfAttendeesText>
          <div data-testid={'chevron'} onClick={toggle}>
            {open ? <BiChevronUp size="24px" /> : <BiChevronDown size="24px" />}
          </div>
        </NumberOfAttendees>
      </GroupBookingInfo>
      {open && (
        <BookingChipContainer>
          {groupMembers.map((member, index: number) => {
            if (index > 0) {
              return (
                <GroupBookingChip
                  key={index}
                  userName={`${member.name}`}
                  isOrganiser={false}
                  onCancel={() => {
                    onDeleteGroupMember(
                      member.userId,
                      userBooking.date,
                      member.name,
                      member.bookingUserId !== member.userId
                        ? member.bookingUserId || ''
                        : member.userId
                    )
                  }}
                  deletable={isDeletable(member.userId, member.bookingUserId)}
                  assignable={false}
                />
              )
            } else return null
          })}
        </BookingChipContainer>
      )}
    </BookingDetailsContainer>
  )
}

export default GroupBooking
